export interface TileOverlayPosition {
  x: number;
  y: number;
}

/**
 * Apply absolute tile overlay positioning with optional scale.
 * Transform origin follows horizontal anchor (left/center/right).
 */
export function applyTileOverlayPosition(
  el: HTMLElement,
  pos: TileOverlayPosition,
  padding = 8,
  scale = 1
): void {
  const clampedScale = Number.isFinite(scale) ? scale : 1;
  const transforms: string[] = [];

  el.style.position = "absolute";
  el.style.maxWidth = "none";
  el.style.maxHeight = "none";

  if (pos.x === 0) {
    el.style.left = `${padding}px`;
    el.style.right = "auto";
    el.style.transformOrigin = "left center";
    if (pos.y === 25 || pos.y === 50 || pos.y === 75) {
      transforms.push("translateY(-50%)");
    }
  } else if (pos.x === 100) {
    el.style.right = `${padding}px`;
    el.style.left = "auto";
    el.style.transformOrigin = "right center";
    if (pos.y === 25 || pos.y === 50 || pos.y === 75) {
      transforms.push("translateY(-50%)");
    }
  } else {
    el.style.left = `${pos.x}%`;
    el.style.right = "auto";
    el.style.transformOrigin = "center center";
    transforms.push("translateX(-50%)");
    if (pos.y === 25 || pos.y === 50 || pos.y === 75) {
      transforms.push("translateY(-50%)");
    }
  }

  if (pos.y === 0) {
    el.style.top = `${padding}px`;
    el.style.bottom = "auto";
  } else if (pos.y === 100) {
    el.style.bottom = `${padding}px`;
    el.style.top = "auto";
  } else {
    el.style.top = `${pos.y}%`;
    el.style.bottom = "auto";
  }

  if (clampedScale !== 1) {
    transforms.push(`scale(${clampedScale})`);
  }

  el.style.transform = transforms.length > 0 ? transforms.join(" ") : "none";
}

export const MANEUVER_OVERLAY_GAP_PX = 8;
export const MANEUVER_OVERLAY_PADDING_PX = 8;
/** Extra inset from tile edges (position padding + breathing room). */
export const MANEUVER_OVERLAY_TILE_INSET_PX = 12;
export const MANEUVER_OVERLAY_MIN_FIT_SCALE = 0.2;
export const MANEUVER_OVERLAY_MAX_FIT_SCALE = 1.15;
export const MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT = 60;
/** TextBox label line above value area at design scale (see TextBox.tsx). */
export const MANEUVER_OVERLAY_TEXTBOX_LABEL_PX = 14;
export const MANEUVER_OVERLAY_TEXTBOX_VALUE_MIN_PX = 40;
/** Minimum TextBox height prop after fit scaling. */
export const MANEUVER_OVERLAY_MIN_TEXTBOX_HEIGHT = 14;
/** Small margin so measured content does not touch tile edge. */
export const MANEUVER_OVERLAY_FIT_SAFETY = 0.96;

/** Raw width ratio without clamp (for height-first fit). */
export function computeTileOverlayWidthRatio(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 1;
  return width / 900;
}

/** Clamp tile overlay scale from container width (reference 900px). */
export function computeTileOverlayScale(width: number): number {
  const raw = computeTileOverlayWidthRatio(width);
  return Math.min(1.15, Math.max(0.5, raw));
}

/** Quantize scale to reduce ResizeObserver churn. */
export function quantizeTileOverlayScale(scale: number): number {
  return Math.round(scale * 100) / 100;
}

export interface ManeuverOverlaySeriesHeightItem {
  height?: number;
}

/** Matches TextBox layout: label row + value min-height derived from builder height prop. */
export function estimateTextBoxRowHeight(designHeight: number): number {
  const scaleFactor = designHeight / MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT;
  return (MANEUVER_OVERLAY_TEXTBOX_LABEL_PX + MANEUVER_OVERLAY_TEXTBOX_VALUE_MIN_PX) * scaleFactor;
}

/** Estimate unscaled overlay content height from builder series config. */
export function estimateManeuverOverlayContentHeight(
  series: ManeuverOverlaySeriesHeightItem[],
  opts?: {
    gap?: number;
    padding?: number;
    orientation?: "horizontal" | "vertical";
    /** When true, stack all channels vertically (e.g. narrow tile or 3+ channels). */
    forceVerticalStack?: boolean;
  }
): number {
  const gap = opts?.gap ?? MANEUVER_OVERLAY_GAP_PX;
  const padding = opts?.padding ?? MANEUVER_OVERLAY_PADDING_PX;
  const orientation = opts?.orientation ?? "vertical";
  const count = series.length;
  if (count === 0) {
    return padding * 2;
  }

  const rowHeights = series.map((item) => item.height ?? MANEUVER_OVERLAY_DEFAULT_ROW_HEIGHT);
  const useVerticalStack =
    opts?.forceVerticalStack === true ||
    orientation === "vertical" ||
    count >= 3;

  if (!useVerticalStack && orientation === "horizontal") {
    const maxDesign = Math.max(...rowHeights);
    return estimateTextBoxRowHeight(maxDesign) + padding * 2;
  }

  const itemsHeight = rowHeights.reduce(
    (sum, designHeight) => sum + estimateTextBoxRowHeight(designHeight),
    0
  );
  const gaps = count > 1 ? gap * (count - 1) : 0;
  return itemsHeight + gaps + padding * 2;
}

export interface ManeuverOverlayFitScaleParams {
  tileWidth: number;
  tileHeight: number;
  contentHeight: number;
  orientation?: "horizontal" | "vertical";
  tilePadding?: number;
}

/** Combined width + height fit scale for maneuver text overlays on video tiles. */
export function computeManeuverOverlayFitScale(params: ManeuverOverlayFitScaleParams): number {
  const {
    tileWidth,
    tileHeight,
    contentHeight,
    tilePadding = MANEUVER_OVERLAY_PADDING_PX,
  } = params;

  if (!Number.isFinite(contentHeight) || contentHeight <= 0) {
    return 1;
  }

  const widthScale = Math.min(
    MANEUVER_OVERLAY_MAX_FIT_SCALE,
    Math.max(MANEUVER_OVERLAY_MIN_FIT_SCALE, computeTileOverlayWidthRatio(tileWidth))
  );
  const availableHeight =
    tileHeight - 2 * tilePadding - MANEUVER_OVERLAY_TILE_INSET_PX;
  const heightScale =
    Number.isFinite(availableHeight) && availableHeight > 0
      ? (availableHeight / contentHeight) * MANEUVER_OVERLAY_FIT_SAFETY
      : MANEUVER_OVERLAY_MIN_FIT_SCALE;

  const raw = Math.min(widthScale, heightScale);
  const clamped = Math.min(
    MANEUVER_OVERLAY_MAX_FIT_SCALE,
    Math.max(MANEUVER_OVERLAY_MIN_FIT_SCALE, raw)
  );
  return quantizeTileOverlayScale(clamped);
}

/** Shrink scale when post-render measurement exceeds available tile height. */
export function refineManeuverOverlayFitScale(
  currentScale: number,
  measuredContentHeight: number,
  availableHeight: number
): number {
  if (
    !Number.isFinite(currentScale) ||
    !Number.isFinite(measuredContentHeight) ||
    !Number.isFinite(availableHeight) ||
    measuredContentHeight <= 0 ||
    availableHeight <= 0 ||
    measuredContentHeight <= availableHeight
  ) {
    return currentScale;
  }

  const adjusted =
    currentScale * (availableHeight / measuredContentHeight) * MANEUVER_OVERLAY_FIT_SAFETY;
  const clamped = Math.min(
    currentScale,
    Math.max(MANEUVER_OVERLAY_MIN_FIT_SCALE, adjusted)
  );
  return quantizeTileOverlayScale(clamped);
}

export function maneuverOverlayAvailableHeight(tileHeight: number): number {
  return Math.max(0, tileHeight - 2 * MANEUVER_OVERLAY_PADDING_PX - MANEUVER_OVERLAY_TILE_INSET_PX);
}

/** Compute fit scale from measured DOM height (most reliable). */
export function computeManeuverOverlayFitScaleFromMeasurement(
  measuredContentHeight: number,
  availableHeight: number,
  currentScale = 1
): number {
  if (
    !Number.isFinite(measuredContentHeight) ||
    !Number.isFinite(availableHeight) ||
    measuredContentHeight <= 0 ||
    availableHeight <= 0
  ) {
    return currentScale;
  }
  if (measuredContentHeight <= availableHeight) {
    return currentScale;
  }
  const adjusted =
    currentScale * (availableHeight / measuredContentHeight) * MANEUVER_OVERLAY_FIT_SAFETY;
  return quantizeTileOverlayScale(
    Math.min(currentScale, Math.max(MANEUVER_OVERLAY_MIN_FIT_SCALE, adjusted))
  );
}
